import { graphql, useStaticQuery } from 'gatsby';
import groupBy from 'lodash.groupby';
import React, { useState } from 'react';
import ContentContainer from '../components/common/ContentContainer';
import Layout from '../components/Layout';
import SEO from '../components/Seo';
import { Staff } from '../components/Staff';
import StaffBio from '../components/StaffBio';

interface MissionStatementSectionProps {}
const MissionStatementSection: React.FC<
  MissionStatementSectionProps
> = props => {
  return (
    <section className="flex flex-col">
      <section className="mt-8 w-3/4 self-center">
        <h1
          className="text-primary font-bold text-center mb-8 text-4xl"
          id="our-story"
        >
          Mission and Vision
        </h1>
        <section className="ml-10">
          <p className="text-gray-500 text-2xl self-center w-full sm:w-2/3 m-auto mt-4 text-center">
            Advancing Literacy is committed to cultivating meaningful learning
            communities through professional development in literacy that is
            research-based, responsive, and inclusive. Our goal is for
            research-based practices to empower educators, engage families, and
            ensure all students develop the literacy skills they need to succeed
            - today and tomorrow.
          </p>
        </section>
      </section>
    </section>
  );
};

interface StaffCardProps {
  staffPerson: Staff;
  staffFallbackImage: any;
  setSelectedStaff: any;
}
const StaffCard: React.FC<StaffCardProps> = props => {
  return (
    <div className="bg-white shadow-md rounded-lg mb-8 h-full">
      <div className="p-6">
        <h3 className="text-2xl text-primary">{props.staffPerson.firstName}</h3>
        <h3 className="text-2xl text-gray-500">{props.staffPerson.lastName}</h3>
        <p className="mt-4 text-primary sans-serif h-16">
          {props.staffPerson.title}
        </p>
      </div>
      <div className="h-64 overflow-hidden bg-gray-700">
        <img
          className="object-cover"
          src={
            props.staffPerson.photo?.file?.url ??
            props.staffFallbackImage.childImageSharp.sizes.src
          }
          alt={`${props.staffPerson.firstName} ${props.staffPerson.lastName} photo`}
        />
      </div>
      <div className="px-6 mt-6">
        <button
          onClick={() => props.setSelectedStaff(props.staffPerson)}
          className="text-primary hover:underline"
        >
          Read Bio &rarr;
        </button>
      </div>
    </div>
  );
};

interface StaffSectionProps {
  staff: Staff[];
  staffFallbackImage: any;
  setSelectedStaff: any;
}

const groupStaffByDepartment = (staff: Staff[]) => {
  const grouped = groupBy(staff, 'category');
  let seniorAndCentral: any = [];
  if (grouped['Senior Leaders']) {
    seniorAndCentral = [...seniorAndCentral, ...grouped['Senior Leaders']];
  }
  if (grouped['Central Office Staff']) {
    seniorAndCentral = [
      ...seniorAndCentral,
      ...grouped['Central Office Staff']
    ];
  }
  return {
    'Senior Staff and Central Office Team': seniorAndCentral || [],
    'Staff Developers': grouped['Staff Developers'] || []
  };
};

const StaffSection: React.FC<StaffSectionProps> = props => {
  const [staff, setStaff] = useState(groupStaffByDepartment(props.staff));
  return (
    <section className="w-5/6 m-auto mt-20">
      <div className="mb-6">
        <div className="flex flex-col">
          <h2 className="text-primary text-4xl font-bold mb-2 text-center">
            Our People
          </h2>
        </div>
      </div>
      <div className="flex flex-col px-12 lg:px-0">
        {Object.keys(staff).map((category: string, i: number) => (
          <div className="" key={i}>
            <h3
              className="text-primary text-2xl font-bold my-6 text-center"
              key={i}
            >
              {category}
            </h3>
            <div className="grid grid-col-1 xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 gap-1 sm:gap-8 m-auto mb-12">
              {staff[category]
                .sort((a: Staff, b: Staff) => a.sortOrder - b.sortOrder)
                .map((s: Staff, i: number) => (
                  <div key={i} className="mb-4 lg:mb-0">
                    <StaffCard
                      staffPerson={s}
                      staffFallbackImage={props.staffFallbackImage}
                      setSelectedStaff={props.setSelectedStaff}
                    />
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const OurPeople: React.FC = props => {
  const [selectedStaff, setSelectedStaff] = useState();
  const data = useStaticQuery(graphql`
    {
      staffFallbackImage: file(
        relativePath: { eq: "images/staff-fallback.png" }
      ) {
        childImageSharp {
          sizes(maxWidth: 600) {
            ...GatsbyImageSharpSizes
          }
        }
      }
      staff: allContentfulStaff(
        sort: { fields: lastName, order: ASC }
        filter: { node_locale: { eq: "en-US" } }
      ) {
        edges {
          node {
            id
            firstName
            lastName
            title
            category
            sortOrder
            bio {
              bio
            }
            photo {
              file {
                url
              }
            }
          }
        }
      }
    }
  `);
  const staff = data.staff.edges.map((e: any) => e.node);
  return (
    <Layout>
      <SEO title="About Advancing Literacy" />
      <div className="mt-4">
        <MissionStatementSection />
      </div>
      <ContentContainer>
        <StaffSection
          staff={staff}
          staffFallbackImage={data.staffFallbackImage}
          setSelectedStaff={setSelectedStaff}
        />
      </ContentContainer>
      {selectedStaff && (
        <div className="relative">
          <StaffBio
            profile={selectedStaff}
            staffFallbackImage={
              data.staffFallbackImage.childImageSharp.sizes.src
            }
            onClose={() => setSelectedStaff(null)}
          />
        </div>
      )}
    </Layout>
  );
};

export default OurPeople;
